/**
 * AcademyInfo Component
 *
 * Displays academy/course information including trailer, image, user progress, and course title/subtitle.
 * Passes relevant props to child components for dynamic rendering and state management.
 *
 * Features:
 * - Conditional rendering of trailer button and modal if available
 * - Displays course image and user progress bar
 * - Shows course title and subtitle
 * - Passes props to child components for modularity and maintainability
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Data object containing course info
 * @param {Object} props.userAditionalTrack - User activity tracking data
 * @param {Object} props.lessons - Lessons data
 * @param {Object} props.accessCourse - Access state for the course
 * @param {Object} props.trailerCourse - Trailer video data
 * @returns {JSX.Element} The rendered academy info section
 */

import { useState } from 'react';
import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';

import UserProgressBar from '../../CourseSpecificInfo/UserProgressBar/UserProgressBar';
import ShowTrailer from '../../CourseSpecificInfo/ShowTrailer/ShowTrailer';

import classes from './AcademyInfo.module.css';

const AcademyInfo = ({
  data,
  userAditionalTrack,
  lessons,
  accessCourse,
  trailerCourse
}) => {
  const [openTrailer, setOpenTrailer] = useState(false);

  /**
   * Handles opening and closing the trailer modal
   */
  const openPreviewVideoHandler = () => {
    setOpenTrailer(!openTrailer);
  };

  return (
    <>
      {/* Trailer button, only if trailer is available */}
      {trailerCourse.httpStatus === 200 && (
        <div className={classes.specificTrailerContainer}>
          <Button
            onClick={openPreviewVideoHandler}
            className={classes.specificTrailerBtn}
          >
            <PlayCircleFilledIcon
              className={classes.playTrailerIcon}
            />
            <p className={classes.trailerText}>
              Ver trailer de este curso
            </p>
          </Button>
        </div>
      )}
      {/* Course image */}
      <figure className={classes.imgCourse}>
        <img
          className={classes.img}
          src={data.imgPathBig}
          alt={data.nombre}
        />
      </figure>
      {/* User progress bar, only if user has access */}
      {accessCourse && (
        <div className={classes.userProgressContainer}>
          <UserProgressBar
            userAditionalTrack={userAditionalTrack} // Prop passed: user activity tracking
            lessons={lessons} // Prop passed: lessons data
          />
        </div>
      )}
      {/* Course title */}
      <p className={classes.courseTitle}>{data.nombre}</p>
      {/* Course subtitle */}
      <p className={classes.courseSubtitle}>{data.nameEmisor}</p>
      {/* Trailer modal, only if trailer is available */}
      {trailerCourse.httpStatus === 200 && (
        <ShowTrailer
          openTrailer={openTrailer} // Prop passed: modal open state
          onClose={openPreviewVideoHandler} // Prop passed: close handler
          trailerData={trailerCourse.response} // Prop passed: trailer video data
        />
      )}
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
AcademyInfo.propTypes = {
  /**
   * Data object containing course info
   * Passed as prop to render course information
   */
  data: PropTypes.shape({
    imgPathBig: PropTypes.string,
    nombre: PropTypes.string,
    nameEmisor: PropTypes.string
  }),
  userAditionalTrack: PropTypes.object,
  lessons: PropTypes.object,
  accessCourse: PropTypes.bool,
  trailerCourse: PropTypes.shape({
    httpStatus: PropTypes.number,
    response: PropTypes.object
  })
};

AcademyInfo.defaultProps = {
  data: { imgPathBig: '', nombre: '', nameEmisor: '' },
  userAditionalTrack: {},
  lessons: {},
  accessCourse: false,
  trailerCourse: { httpStatus: 0, response: {} }
};

export default AcademyInfo;
